'use client';

import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { useCallback, useState } from 'react';

interface AvatarProps {
   name?: string;
   src?: string | null;
   className?: string;
   loading?: 'lazy' | 'eager';
}

export function Avatar({ name, src, className, loading }: AvatarProps) {
   const [failed, setFailed] = useState(false);
   // A server-rendered image can fail before hydration attaches onError.
   const checkFailed = useCallback((img: HTMLImageElement | null) => {
      if (img?.complete && img.naturalWidth === 0) setFailed(true);
   }, []);
   const baseClassName =
      'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full';

   if (src && !failed) {
      return (
         <img
            ref={checkFailed}
            src={src}
            alt=""
            loading={loading}
            onError={() => setFailed(true)}
            className={cn(baseClassName, 'object-cover', className)}
         />
      );
   }

   if (!name) {
      return (
         <span aria-hidden="true" className={cn(baseClassName, 'bg-gray-400', className)}>
            <FaIcon name="user" className="size-1/2 text-white" />
         </span>
      );
   }

   const background = colorFromName(name);
   return (
      <span
         aria-hidden="true"
         className={cn(
            baseClassName,
            isDarkColor(background) ? 'text-white' : 'text-gray-800',
            className
         )}
         style={{ backgroundColor: background }}
      >
         {initials(name)}
      </span>
   );
}

// Same name hash as Chakra's Avatar, so an existing user keeps their colour.
export function colorFromName(name: string): string {
   let hash = 0;
   for (let i = 0; i < name.length; i += 1) {
      // charCodeAt and the 32-bit wrap are what Chakra hashed; codePointAt or
      // Math.trunc would change colours for some names.
      // eslint-disable-next-line unicorn/prefer-code-point, unicorn/prefer-math-trunc
      hash = (name.charCodeAt(i) + ((hash << 5) - hash)) | 0;
   }
   let color = '#';
   for (let j = 0; j < 3; j += 1) {
      const value = (hash >> (j * 8)) & 255;
      color += `00${value.toString(16)}`.slice(-2);
   }
   return color;
}

export function isDarkColor(hex: string): boolean {
   const [r, g, b] = [1, 3, 5].map(i => Number.parseInt(hex.slice(i, i + 2), 16));
   return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

// Chakra's Avatar rule: first and last name, so "Jane Q Public" stays "JP".
export function initials(name: string): string {
   const names = name.trim().split(' ');
   const first = names[0] ?? '';
   const last = names.length > 1 ? (names.at(-1) ?? '') : '';
   return first && last ? `${first.charAt(0)}${last.charAt(0)}` : first.charAt(0);
}
